<template>
  <form class="invite-form">
    <!-- Employee Information Section -->
    <div class="form-section">
      <div class="section-header">
        <div class="section-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 10C12.0711 10 13.75 8.32107 13.75 6.25C13.75 4.17893 12.0711 2.5 10 2.5C7.92893 2.5 6.25 4.17893 6.25 6.25C6.25 8.32107 7.92893 10 10 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16.4416 17.5C16.4416 14.4625 13.5558 12 9.99993 12C6.44409 12 3.55826 14.4625 3.55826 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="section-title-group">
          <h3 class="section-title">Employee Information</h3>
          <p class="section-subtitle">Enter the employee's basic details</p>
        </div>
      </div>

      <div class="form-grid">
        <div class="form-row full-width">
          <InputField
            :validate="validateForm"
            label="Employee name"
            type="text"
            name="name"
            placeholder="Enter full name"
            :currentValue="employee.name"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </div>
        <div class="form-row">
          <InputField
            :validate="validateForm"
            label="Employee email"
            type="email"
            name="email"
            placeholder="employee@company.com"
            :currentValue="employee.email"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </div>
        <div class="form-row">
          <InputField
            :validate="validateForm"
            label="Role"
            type="text"
            name="role"
            placeholder="e.g., Software Engineer"
            :currentValue="employee.role"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </div>
      </div>

      <div class="info-notice">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 10.6667V8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 5.33334H8.00667" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>You are strongly advised to use employee's personal email</span>
      </div>
    </div>

    <!-- Stock Option Type Section -->
    <div class="form-section">
      <div class="section-header">
        <div class="section-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.5 8.33333V12.5C17.5 16.6667 15.8333 18.3333 11.6667 18.3333H8.33333C4.16667 18.3333 2.5 16.6667 2.5 12.5V7.5C2.5 3.33333 4.16667 1.66667 8.33333 1.66667H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M17.5 8.33333H14.1667C11.6667 8.33333 10.8333 7.5 10.8333 5V1.66667L17.5 8.33333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5.83334 10.8333H10.8333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5.83334 14.1667H8.33334" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="section-title-group">
          <h3 class="section-title">Stock Option Type</h3>
          <p class="section-subtitle">Choose the type of stock option plan for this employee</p>
        </div>
      </div>

      <div class="option-cards">
        <label 
          v-for="(option, index) in stockOptionTypes" 
          :key="index"
          class="option-card"
          :class="{ 'option-card--active': StockType === option.value }"
        >
          <input
            v-model="StockType"
            type="radio"
            name="stockOption"
            :value="option.value"
            class="option-radio"
          />
          <div class="option-content">
            <div class="option-indicator">
              <div class="indicator-dot" v-if="StockType === option.value"></div>
            </div>
            <div class="option-details">
              <span class="option-name">{{ option.name }}</span>
              <span class="option-description">{{ option.description }}</span>
            </div>
          </div>
        </label>
      </div>
    </div>

    <!-- Dynamic Form Component -->
    <div class="form-section">
      <component
        :is="StockType"
        @submit="saveFormValue"
        @validate="checkFormValidation"
      />
    </div>
  </form>
</template>

<script>
import { mapGetters } from "vuex";
import InputField from "@/components/general/InputField";
import ESOP_EMPLOYEE from "../forms/EsoForm";
import RSU_EMPLOYEE from "../forms/RsuForm";

export default {
  name: "EmployeeInvite",
  
  data() {
    return {
      employee: {
        email: "",
        name: "",
        role: "",
        vesting_duration: "",
        vesting_frequency: "",
        allocated_shares: "",
        cliff_period: "",
        strike_price: "",
        start_date: "",
      },
      stockOptionTypes: [
        {
          name: "Employee Stock Ownership Plan (ESOP)",
          value: "ESOP_EMPLOYEE",
          description: "Traditional stock options with exercise price and vesting"
        },
        {
          name: "Restricted Stock Units (RSU)",
          value: "RSU_EMPLOYEE",
          description: "Company shares granted upon vesting completion"
        },
      ],
      validationResult: [],
      validateForm: false,
      StockType: "ESOP_EMPLOYEE",
    };
  },
  
  components: {
    ESOP_EMPLOYEE,
    RSU_EMPLOYEE,
    InputField,
  },
  
  emits: ["submit"],
  
  watch: {
    employeeInfo(val) {
      if (val) {
        this.fillForm();
      }
    },
    validationResult: {
      deep: true,
      handler(val) {
        if (this.StockType !== "ESOP_EMPLOYEE") {
          delete this.employee.strike_price;
          delete this.employee.exercise_timeline;
        }
        if (
          val.length === Object.keys(this.employee).length - 1 &&
          !val.includes(false)
        ) {
          this.sendInviteInfo();
        }
      },
    },
    strikePriceFormatted(val) {
      this.strike_price = val;
    },
    sharesFormatted(val) {
      this.allocated_shares = val;
    },
  },
  
  computed: {
    ...mapGetters("Esop", ["employeeInfo"]),
  },
  
  mounted() {
    if (this.$route.query.id) {
      // Edit mode
    }
  },
  
  methods: {
    handleValidation(value) {
      this.validationResult.push(value);
    },
    
    storeValue(object) {
      if (
        object.name !== "strike_price" &&
        object.name !== "allocated_shares"
      ) {
        this.employee[object.name] = object.value;
      } else {
        this[object.name] = object.value;
      }
    },
    
    saveFormValue(obj) {
      this.employee = {
        ...this.employee,
        ...obj.payload,
      };
    },
    
    sendInviteInfo() {
      let payload = {
        email: this.employee.email,
        name: this.employee.name,
        role: this.employee.role,
        vesting_duration: String(this.employee.vesting_duration),
        vesting_frequency: this.employee.vesting_frequency,
        allocated_shares: String(this.employee.allocated_shares),
        cliff_period: String(this.employee.cliff_period),
        start_date: this.employee.start_date,
        employee_type: this.employee.employee_type,
        documents: this.employee.documents,
      };
      if (this.StockType === "ESOP_EMPLOYEE") {
        payload.strike_price = String(this.employee.strike_price);
        payload.exercise_timeline = String(this.employee.exercise_timeline);
      }
      this.$emit("submit", payload);
    },
    
    checkFormValidation(validationResult) {
      this.validationResult = [];
      this.validateForm = true;
      this.validationResult = [...validationResult];
      setTimeout(() => {
        this.validateForm = false;
      }, 1000);
    },
    
    fillForm() {
      if (this.employeeInfo.employee_type === "RSU_EMPLOYEE") {
        this.StockType = "RSU_EMPLOYEE";
        this.stockOptionTypes = [
          {
            name: "Restricted Stock Units (RSU)",
            value: "RSU_EMPLOYEE",
            description: "Company shares granted upon vesting completion"
          },
        ];
      } else {
        this.StockType = "ESOP_EMPLOYEE";
        this.stockOptionTypes = [
          {
            name: "Employee Stock Ownership Plan (ESOP)",
            value: "ESOP_EMPLOYEE",
            description: "Traditional stock options with exercise price and vesting"
          },
        ];
      }
      this.employee.email = this.employeeInfo.invitation.email;
      this.employee.name = this.employeeInfo.invitation.name;
      this.employee.role = this.employeeInfo.role;
    },
  },
};
</script>

<style lang="scss" scoped>
.invite-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #F1F5F9;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #EEF2FF;
  border-radius: 10px;
  color: #4D84FF;
  flex-shrink: 0;
}

.section-title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.section-subtitle {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.form-row {
  &.full-width {
    grid-column: 1 / -1;
  }
}

.info-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  margin-top: 20px;
  color: #B45309;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
}

.option-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #CBD5E1;
    background: #F1F5F9;
  }

  &--active {
    background: #EEF2FF;
    border-color: #4D84FF;

    &:hover {
      background: #EEF2FF;
      border-color: #4D84FF;
    }

    .option-indicator {
      border-color: #4D84FF;
      background: white;
    }

    .indicator-dot {
      width: 10px;
      height: 10px;
      background: #4D84FF;
      border-radius: 50%;
    }

    .option-name {
      color: #4D84FF;
    }
  }
}

.option-radio {
  display: none;
}

.option-content {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}

.option-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid #CBD5E1;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.option-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-name {
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  transition: color 0.2s ease;
}

.option-description {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
}
</style>
